import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Mousetrap from 'mousetrap';
import classNames from 'classnames';
import { FormGroup, FormControl, FormCheck, Button } from 'react-bootstrap';
import { useLocalStorage } from '../../util/hooks';
import QuantityLine from './QuantityLine';

const originalKey = Mousetrap.prototype.handleKey;

const GoodScan = ({ title = '' }) => {
  return (
    <span className="good-scan" title={title}>
      <i className="fas fa-check-circle" />
    </span>
  );
};

GoodScan.propTypes = {
  title: PropTypes.string,
};

const BadScan = ({ title = '' }) => {
  return (
    <span className="bad-scan" title={title}>
      <i className="fa-solid fa-octagon-exclamation" />
    </span>
  );
};

BadScan.propTypes = {
  title: PropTypes.string,
};

const QuantityPane = props => {
  const { initialQty, onChange, threshold, item = '', allBins = [], initialQtyLines = [], bin } = props;
  const [qty, setQty] = useState(initialQty || 0);
  const [qtyLines, setQtyLines] = useState(initialQtyLines.length ? initialQtyLines : [...allBins.map(() => 0)]);
  const [scannedItem, setScannedItem] = useState('');
  const [lastScannedPart, setLastScannedPart] = useState(null);
  const [lastScannedStatus, setLastScannedStatus] = useState([]);
  const [hasFocus, setHasFocus] = useState(null);

  const [featureEnabled, setFeatureEnabled] = useLocalStorage('scanItemQuantity', false);

  const matches = item.match(/^[A-Z]?:?([A-Z.#*0-9/-]+)$/i);
  const baseItem = Array.isArray(matches) ? matches[1] : '';

  const itemMatch = new RegExp(`^[A-Z]?:?${baseItem}$`);

  const firstUpdate = useRef(true);

  const onQtyFocus = () => {
    setHasFocus(true);
  };

  const onQtyBlur = () => {
    setHasFocus(false);
    onChange(qty, allBins.length === 1 ? [qty] : qtyLines);
  };

  // TODO Issue with this causing component to rerender and losing state
  const updateQtyDownstream = () => {
    onChange(qty, allBins.length === 1 ? [qty] : qtyLines);
  };

  const handleDecrement = () => {
    const newQty = qty - 1;
    if (newQty < 0) {
      return;
    }

    setQty(newQty);
  };

  const handleIncrement = () => {
    const newQty = qty + 1;
    setQty(newQty);
  };

  useEffect(() => {
    Mousetrap.bind(['+'], handleIncrement);
    Mousetrap.bind(['-'], handleDecrement);

    return () => {
      Mousetrap.unbind(['+']);
      Mousetrap.unbind(['-']);
    };
  });

  const updateLine = (value, num) => {
    const newList = qtyLines.map((line, index) => {
      if (num !== index) {
        return line;
      }

      return value;
    });

    setQtyLines(newList);
    setQty(newList.reduce((a, b) => a + b));
  };

  const onQtyChange = event => {
    if (String(event.target.value).length > 4) {
      return;
    }

    if (event.target.value === '') {
      setQty(0);
      return;
    }

    const newQty = parseInt(event.target.value);

    if (isNaN(newQty)) {
      return;
    }

    setQty(parseInt(event.target.value));
  };

  const handleKey = (char, m, e) => {
    if (featureEnabled && !hasFocus) {
      let newScannedItem = scannedItem;

      if (e.target.tagName !== 'INPUT') {
        if (e.type === 'keydown') {
          if (e.key === 'Tab' || e.key === 'Enter') {
            if (newScannedItem === '') {
              e.preventDefault();
              return;
            }

            setLastScannedPart(scannedItem);
            newScannedItem = '';
            setScannedItem(newScannedItem);
            e.preventDefault();

            if (itemMatch.test(scannedItem)) {
              handleIncrement();
              setLastScannedStatus([true, ...lastScannedStatus]);
            } else {
              setLastScannedStatus([false, ...lastScannedStatus]);
            }
            return;
          }
        }

        if (e.type === 'keypress' && e.key !== 'Tab' && e.key !== 'Enter') {
          setScannedItem(`${newScannedItem}${e.key}`.toUpperCase());
        }
      }
    }
  };

  useEffect(() => {
    Mousetrap.prototype.handleKey = handleKey;

    return () => {
      Mousetrap.prototype.handleKey = originalKey;
    };
  });

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    if (!hasFocus) {
      updateQtyDownstream();
    }
  }, [qty]);

  const showWarning = () => {
    if (threshold !== null && qty > threshold) {
      return true;
    }

    return false;
  };

  const renderAmountWarning = () => {
    return (
      <div className={warningClassName}>
        <i className="fa-solid fa-triangle-exclamation" /> <span>Greater than designated amount</span>
      </div>
    );
  };

  const warningClassName = classNames('qty-warning', {
    disabled: !showWarning(),
  });

  if (allBins.length > 1) {
    return (
      <>
        {renderAmountWarning()}
        <div className="alt-bins">
          <div className="title">
            <div>Multiple Bins</div>
            Part <strong>{item}</strong>
          </div>
          {qtyLines.map((qty, index) => {
            const label = allBins[index] === bin ? `${allBins[index]}*` : allBins[index];
            return (
              <QuantityLine key={index} label={label} updateLine={updateLine} initialQty={qty} lineNumber={index} />
            );
          })}
          <div className="total">
            Total <strong>{qty}</strong>
          </div>
        </div>
      </>
    );
  }

  const scanStatus = hasFocus ? 'Manual Input...' : 'Ready.';

  return (
    <>
      {renderAmountWarning()}
      {item !== '' && (
        <div className="item-label">
          <strong>{item}</strong>
        </div>
      )}
      <div className="controls">
        <div className={classNames('decrement', { disabled: qty === 0 })} onClick={handleDecrement}>
          <i className="fa-solid fa-minus fa-fw" />
        </div>
        <div className="quant">
          <FormGroup>
            <FormControl onChange={onQtyChange} onFocus={onQtyFocus} onBlur={onQtyBlur} value={qty} />
          </FormGroup>
        </div>
        <div className="increment" onClick={handleIncrement}>
          <i className="fa-solid fa-plus fa-fw" />
        </div>
      </div>
      <div style={{ minHeight: '250px' }}>
        {item !== '' && (
          <div className="increment-scan">
            <div className="chk">
              <FormCheck style={{ paddingLeft: '0px' }} className="mb-2">
                <FormCheck.Input
                  type="checkbox"
                  id="cc-chk-fe"
                  onChange={() => setFeatureEnabled(!featureEnabled)}
                  defaultChecked={featureEnabled}
                  onMouseDown={e => e.preventDefault()}
                />
                <FormCheck.Label htmlFor="cc-chk-fe">Increment Scanning</FormCheck.Label>
              </FormCheck>
            </div>
            {featureEnabled ? (
              <div className="scan-enabled">
                <div className="scanned-item">{scannedItem === '' ? scanStatus : scannedItem}&nbsp;</div>
                {hasFocus && (
                  <Button className="mt-2" style={{ width: '100%' }}>
                    Exit Manual Input
                  </Button>
                )}
                <div className="last-scanned">
                  {lastScannedStatus.map((status, index) => {
                    if (index === 0) {
                      return status === true ? (
                        <GoodScan key={index} title={lastScannedPart} />
                      ) : (
                        <BadScan key={index} title={lastScannedPart} />
                      );
                    }

                    return status === true ? <GoodScan key={index} /> : <BadScan key={index} />;
                  })}
                </div>
                {lastScannedStatus.length > 0 && (
                  <div className={classNames('last-scanned-item', { good: lastScannedStatus[0] })}>
                    {lastScannedPart}
                  </div>
                )}
              </div>
            ) : (
              <div className="details">
                Increment on successive part scans. If the scan matches the currently active part then the quantity will
                increment. If there is no match, you will be notified.
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

QuantityPane.propTypes = {
  allBins: PropTypes.array,
  bin: PropTypes.string,
  initialQty: PropTypes.number,
  initialQtyLines: PropTypes.array,
  item: PropTypes.string,
  onChange: PropTypes.func,
  threshold: PropTypes.number,
};

export default QuantityPane;
